// v2.4.0 - 2026-02-02 - Admin Dashboard - Period range picker (from/to)

import { useCallback, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/stores/authStore'
import { useQuery } from '@/lib/hooks'
import { cer } from '@/lib/api/client'
// Types imported via cer API namespace
import { PageContainer, PageHeader } from '@/components/layout'
import { Card, CardContent, CardHeader, CardTitle, Badge, Button, Spinner } from '@/components/ui'
import { KPICard, KPIIcons } from '@/components/admin'
import {
  ComposedChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts'

// Singleton formatters
const numberFormatter = new Intl.NumberFormat('it-IT', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
})

const currencyFormatter = new Intl.NumberFormat('it-IT', {
  style: 'currency',
  currency: 'EUR',
})

function formatNumber(num: number): string {
  return numberFormatter.format(num)
}

function formatCurrency(num: number): string {
  return currencyFormatter.format(num)
}

function formatPeriod(year: number, month: number): string {
  const monthNames = [
    'Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu',
    'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic',
  ]
  return `${monthNames[month - 1]} ${year}`
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    draft: 'Bozza',
    approved: 'Approvato',
    distributed: 'Distribuito',
    pending: 'In Attesa',
    completed: 'Completato',
    error: 'Errore',
  }
  return labels[status] || status
}

function getStatusBadgeVariant(status: string): 'default' | 'success' | 'warning' | 'error' | 'info' {
  const variants: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
    draft: 'default',
    approved: 'success',
    distributed: 'info',
    pending: 'warning',
    completed: 'success',
    error: 'error',
  }
  return variants[status] || 'default'
}

// Chart colors
const CHART_COLORS = {
  immessa: '#22c55e', // green-500
  prelevata: '#3b82f6', // blue-500
  eaci: '#f59e0b', // amber-500
}

const PIE_COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#8b5cf6']

// Period range picker component (from/to dropdowns)
function PeriodRangePicker({
  periods,
  fromPeriod,
  toPeriod,
  onFromChange,
  onToChange,
  onReset
}: {
  periods: string[]
  fromPeriod: string
  toPeriod: string
  onFromChange: (period: string) => void
  onToChange: (period: string) => void
  onReset: () => void
}) {
  const formatPeriodLabel = (period: string): string => {
    const [year, month] = period.split('-')
    const monthNames = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre']
    return `${monthNames[parseInt(month) - 1]} ${year}`
  }

  const hasFilter = fromPeriod !== periods[0] || toPeriod !== periods[periods.length - 1]

  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="text-sm font-medium text-[var(--color-text-secondary)]">Periodo:</span>
      <div className="flex items-center gap-2">
        <span className="text-sm text-[var(--color-text-secondary)]">Da</span>
        <select
          value={fromPeriod}
          onChange={(e) => onFromChange(e.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-1.5 text-sm"
        >
          {periods.map(p => (
            <option key={p} value={p} disabled={p > toPeriod}>
              {formatPeriodLabel(p)}
            </option>
          ))}
        </select>
        <span className="text-sm text-[var(--color-text-secondary)]">a</span>
        <select
          value={toPeriod}
          onChange={(e) => onToChange(e.target.value)}
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-1.5 text-sm"
        >
          {periods.map(p => (
            <option key={p} value={p} disabled={p < fromPeriod}>
              {formatPeriodLabel(p)}
            </option>
          ))}
        </select>
      </div>
      {hasFilter && (
        <Button variant="ghost" size="sm" onClick={onReset}>
          Tutti i periodi
        </Button>
      )}
    </div>
  )
}

export default function AdminDashboard() {
  const navigate = useNavigate()
  const { user } = useAuthStore()

  // Filter state
  const [selectedConfigId, setSelectedConfigId] = useState<string>('')
  const [fromPeriod, setFromPeriod] = useState<string>('')
  const [toPeriod, setToPeriod] = useState<string>('')

  // Fetch dashboard data without period filter - we filter client-side
  const fetchDashboard = useCallback(async () => {
    if (!user?.cer_id) return null
    const filters: { configId?: string } = {}
    if (selectedConfigId) filters.configId = selectedConfigId
    return cer.getAdminDashboard(user.cer_id, Object.keys(filters).length > 0 ? filters : undefined)
  }, [user?.cer_id, selectedConfigId])

  const { data, isLoading, error } = useQuery(fetchDashboard, {
    enabled: !!user?.cer_id,
  })

  // Extract available periods from API data (YYYY-MM format)
  const availablePeriods = useMemo(() => {
    if (!data?.filterOptions?.availablePeriods?.length) return []
    return data.filterOptions.availablePeriods
      .map(p => `${p.year}-${String(p.month).padStart(2, '0')}`)
      .sort() // Oldest first
  }, [data?.filterOptions?.availablePeriods])

  // Initialize period range to full range when data loads
  const effectiveFromPeriod = fromPeriod || availablePeriods[0] || ''
  const effectiveToPeriod = toPeriod || availablePeriods[availablePeriods.length - 1] || ''

  const handleResetPeriods = () => {
    setFromPeriod('')
    setToPeriod('')
  }

  // Memoized chart data - filtered by period range
  const chartData = useMemo(() => {
    if (!data?.energyTrend) return []

    // Filter by period range
    const filtered = data.energyTrend.filter(point => {
      // point.period is in format "Gen 25" - need to convert back to YYYY-MM
      const monthNames = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic']
      const parts = point.period.split(' ')
      if (parts.length !== 2) return true // Include if can't parse
      const monthIndex = monthNames.indexOf(parts[0])
      const year = `20${parts[1]}`
      if (monthIndex === -1) return true
      const periodKey = `${year}-${String(monthIndex + 1).padStart(2, '0')}`
      return periodKey >= effectiveFromPeriod && periodKey <= effectiveToPeriod
    })

    return filtered.map((point) => ({
      period: point.period,
      Immessa: point.immessa,
      Prelevata: point.prelevata,
      EACI: point.eaci,
    }))
  }, [data?.energyTrend, effectiveFromPeriod, effectiveToPeriod])

  // Distribution pie data for latest month
  const pieData = useMemo(() => {
    if (!data?.kpis) return []
    const { immessaKwh, prelevataKwh, eaciKwh } = data.kpis
    if (immessaKwh === 0 && prelevataKwh === 0) return []
    return [
      { name: 'Immessa', value: immessaKwh },
      { name: 'Prelevata', value: prelevataKwh },
      { name: 'EACI', value: eaciKwh },
    ].filter((d) => d.value > 0)
  }, [data?.kpis])

  if (isLoading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      </PageContainer>
    )
  }

  if (error) {
    return (
      <PageContainer>
        <PageHeader title="Dashboard" description="Panoramica della CER" />
        <div className="rounded-md bg-red-50 p-4 text-sm text-red-600">
          Errore nel caricamento della dashboard: {error}
        </div>
      </PageContainer>
    )
  }

  if (!data) {
    return (
      <PageContainer>
        <PageHeader title="Dashboard" description="Panoramica della CER" />
        <Card>
          <CardContent className="py-12 text-center text-[var(--color-text-secondary)]">
            Nessun dato disponibile
          </CardContent>
        </Card>
      </PageContainer>
    )
  }

  const { kpis, recentUploads, recentCalculations } = data

  return (
    <PageContainer>
      <PageHeader
        title="Dashboard"
        description="Panoramica della CER"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/admin/upload')}>
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Carica Dati
            </Button>
            <Button onClick={() => navigate('/admin/calculations')}>
              <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
              </svg>
              Nuovo Calcolo TI
            </Button>
          </div>
        }
      />

      <div className="space-y-6">
        {/* Filters */}
        {data?.filterOptions && (
          <Card>
            <CardContent className="py-4">
              <div className="flex flex-wrap items-center gap-6">
                {/* Configuration filter */}
                <div className="flex items-center gap-2">
                  <label className="text-sm font-medium text-[var(--color-text-secondary)]">
                    Configurazione:
                  </label>
                  <select
                    value={selectedConfigId}
                    onChange={(e) => setSelectedConfigId(e.target.value)}
                    className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-primary)] px-3 py-1.5 text-sm"
                  >
                    <option value="">Tutte</option>
                    {data.filterOptions.configurations.map((config) => (
                      <option key={config.id} value={config.id}>
                        {config.crin}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Period range picker */}
                {availablePeriods.length > 0 && (
                  <PeriodRangePicker
                    periods={availablePeriods}
                    fromPeriod={effectiveFromPeriod}
                    toPeriod={effectiveToPeriod}
                    onFromChange={setFromPeriod}
                    onToChange={setToPeriod}
                    onReset={handleResetPeriods}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* KPI Cards Row */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KPICard
            title="Energia Immessa"
            value={kpis.immessaKwh}
            unit="kWh"
            trend={kpis.immessaTrend}
            variant="success"
            icon={KPIIcons.upload}
          />
          <KPICard
            title="Energia Prelevata"
            value={kpis.prelevataKwh}
            unit="kWh"
            trend={kpis.prelevataTrend}
            variant="info"
            icon={KPIIcons.download}
          />
          <KPICard
            title="EACI Condivisa"
            value={kpis.eaciKwh}
            unit="kWh"
            trend={kpis.eaciTrend}
            variant="warning"
            icon={KPIIcons.shared}
          />
          <KPICard
            title="TI Totale"
            value={kpis.tiTotalEur}
            unit="EUR"
            variant="default"
            icon={KPIIcons.currency}
          />
        </div>

        {/* Charts Row */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Energy Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Trend Energia (ultimi 6 mesi)</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData}>
                      <defs>
                        <linearGradient id="gradientImmessa" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={CHART_COLORS.immessa} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={CHART_COLORS.immessa} stopOpacity={0.05} />
                        </linearGradient>
                        <linearGradient id="gradientPrelevata" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={CHART_COLORS.prelevata} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={CHART_COLORS.prelevata} stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="period" tick={{ fontSize: 12 }} stroke="var(--color-text-secondary)" />
                      <YAxis tick={{ fontSize: 12 }} stroke="var(--color-text-secondary)" />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'var(--color-bg-primary)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px',
                        }}
                        formatter={(value: number) => `${formatNumber(value)} kWh`}
                      />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="Immessa"
                        stroke={CHART_COLORS.immessa}
                        strokeWidth={2}
                        fill="url(#gradientImmessa)"
                      />
                      <Area
                        type="monotone"
                        dataKey="Prelevata"
                        stroke={CHART_COLORS.prelevata}
                        strokeWidth={2}
                        fill="url(#gradientPrelevata)"
                      />
                      <Line
                        type="monotone"
                        dataKey="EACI"
                        stroke={CHART_COLORS.eaci}
                        strokeWidth={2}
                        dot={{ fill: CHART_COLORS.eaci }}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-[var(--color-text-secondary)]">
                  Nessun dato trend disponibile
                </div>
              )}
            </CardContent>
          </Card>

          {/* Distribution Pie Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Distribuzione Energia (mese corrente)</CardTitle>
            </CardHeader>
            <CardContent>
              {pieData.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        fill="#8884d8"
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number) => `${formatNumber(value)} kWh`}
                        contentStyle={{
                          backgroundColor: 'var(--color-bg-primary)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px',
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-[var(--color-text-secondary)]">
                  Nessun dato distribuzione disponibile
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity Row */}
        <div className="grid gap-6 lg:grid-cols-2">
          {/* Recent Uploads */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Ultimi Upload</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin/energy-data')}
                >
                  Vedi dati
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {recentUploads.length > 0 ? (
                <div className="space-y-3">
                  {recentUploads.slice(0, 5).map((upload) => (
                    <Link
                      key={upload.id}
                      to={`/admin/energy-data?configId=${upload.configId}&year=${upload.year}&month=${upload.month}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate">{upload.filename}</p>
                        <p className="text-sm text-[var(--color-text-secondary)]">
                          {upload.configCrin} | {formatPeriod(upload.year, upload.month)} | {upload.rowsParsed} righe
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <Badge variant={getStatusBadgeVariant(upload.status)}>
                          {getStatusLabel(upload.status)}
                        </Badge>
                        <span className="text-xs text-[var(--color-text-secondary)]">
                          {formatDate(upload.uploadedAt)}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-[var(--color-text-secondary)] text-center py-6">
                  Nessun upload recente
                </p>
              )}
            </CardContent>
          </Card>

          {/* Recent TI Calculations */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Calcoli TI Recenti</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/admin/calculations')}
                >
                  Vedi tutti
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {recentCalculations.length > 0 ? (
                <div className="space-y-3">
                  {recentCalculations.slice(0, 5).map((calc) => (
                    <Link
                      key={calc.id}
                      to={`/admin/calculations/${calc.id}`}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                    >
                      <div>
                        <p className="font-medium">{formatPeriod(calc.year, calc.month)}</p>
                        <p className="text-sm text-[var(--color-text-secondary)]">
                          {calc.configCrin} | EACI: {formatNumber(calc.eaciKwh)} kWh
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="font-semibold">{formatCurrency(calc.tiTotalEur)}</p>
                          <Badge variant={getStatusBadgeVariant(calc.status)}>
                            {getStatusLabel(calc.status)}
                          </Badge>
                        </div>
                        <svg className="h-4 w-4 text-[var(--color-text-secondary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <p className="text-[var(--color-text-secondary)] text-center py-6">
                  Nessun calcolo TI recente
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Azioni Rapide</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => navigate('/admin/energy-data')}>
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                </svg>
                Browser Dati Energia
              </Button>
              <Button variant="outline" onClick={() => navigate('/admin/members')}>
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                Gestione Membri
              </Button>
              <Button variant="outline" onClick={() => navigate('/admin/configurations')}>
                <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Configurazioni
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  )
}
